import {
  Edit,
  SimpleForm,
  TextInput,
  NumberInput,
  Labeled,
  useEditContext,
  ImageField,
  useTranslate,
  SelectArrayInput,
  useDeleteMany,
  SelectInput,
  useResourceContext,
  BooleanInput,
  useCreate,
  Toolbar,
  DeleteButton,
  useUpdate
} from "react-admin";
import { yupResolver } from "@hookform/resolvers/yup";
import { usePhotoUploadSubmitEdit } from "hooks/usePhotoUploadSubmitEdit";
import * as yup from "yup";
import * as User from "providers/user";
import { RichTextInput } from "ra-input-rich-text";
import { ReferenceManyToManyInput } from "@react-admin/ra-relationships";
import CustomHeaderActions from "./CustomHeaderActions";
import ShowAndEditDetails from "./ShowAndEditDetails";
import { AddOnsDetailsIcon, ServiceDetailsIcon } from "assets/Icons";
import CustomImageInputWrapper from "./CustomImageInputWrapper";
import {
  AddOnsSchedulingPriorities,
  ServiceCategoryChoices
} from "./ServiceCreate";
import { useNavigate } from "react-router-dom";
import { ArtistExclusions } from "./ServiceArtistExclusion";
import { useState } from "react";
import CustomSaveButton from "./CustomSaveButton";
import CustomEditableImageInput from "./CustomEditableImageInput";

function ServiceImages({ imageKey }: { imageKey: string }) {
  const { record } = useEditContext();
  const translate = useTranslate();

  if (record?.[imageKey]) {
    return (
      <Labeled label={translate(`services.${imageKey.replace("_url", "")}`)}>
        <ImageField source={imageKey} />
      </Labeled>
    );
  }
  return null;
}

function CustomToolbar({
  isAddOn,
  isLoading
}: {
  isAddOn: boolean;
  isLoading: boolean;
}) {
  const navigate = useNavigate();
  const { record } = useEditContext();
  const [deleteMany] = useDeleteMany();

  const handleDelete = async () => {
    if (isAddOn) {
      await deleteMany(
        "ServiceAddOn",
        {
          ids: record.core_services.map((service: any) => service.id)
        },
        {
          onSuccess: () => navigate("/dashboard/AddOns")
        }
      );
    }
  };

  return (
    <Toolbar
      style={{ display: "flex", justifyContent: "space-between" }}
      sx={{
        backgroundColor: "rgba(250, 249, 247, 1)",
        minHeight: "44px",
        marginBottom: "24px",
        "@media screen and (max-width:640px)": {
          marginBottom: "0px",
          zIndex: "11 !important"
        },
        "& .MuiButton-containedPrimary": {
          borderRadius: "32px",
          backgroundColor: "rgba(34, 46, 66, 1)",
          fontSize: "14px",
          textTransform: "capitalize",
          boxShadow: "none",
          "&:hover": {
            backgroundColor: "rgba(22, 30, 44, 1)"
          }
        },
        "& .MuiButtonBase-root.Mui-disabled": {
          backgroundColor: "rgba(48, 47, 46, 0.12)"
        },
        "& .ra-delete-button": {
          textTransform: "capitalize"
        }
      }}
    >
      <CustomSaveButton isLoading={isLoading} />
      <DeleteButton onClick={handleDelete} />
    </Toolbar>
  );
}

export default function ServiceEdit() {
  const { permissions } = User.useUser();
  const translate = useTranslate();
  const resource = useResourceContext();
  const [create] = useCreate();
  const [edit] = useUpdate();
  const [deleteMany] = useDeleteMany();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isAddOn = resource === "AddOns";
  const imageKeys = [
    "photo_1_url",
    "photo_2_url",
    "photo_3_url",
    "photo_4_url",
    "photo_5_url"
  ];
  const { submit: submitWithPhoto } = usePhotoUploadSubmitEdit({
    redirect: `/dashboard/Service`,
    resource: "Service",
    keys: imageKeys,
    setIsLoading: setIsLoading
  });

  const submit = async (data: any) => {
    setIsLoading(true);
    const payload = {
      ...data,
      clean_up_time: data?.clean_up_time ? data.clean_up_time : 0,
      description: data?.description ? data.description : "",
      special_conditions: data?.special_conditions
        ? data.special_conditions
        : "",
      photo_1_url: data?.photo_1_url ? data.photo_1_url : "",
      photo_2_url: data?.photo_2_url ? data.photo_2_url : "",
      photo_3_url: data?.photo_3_url ? data.photo_3_url : "",
      photo_4_url: data?.photo_4_url ? data.photo_4_url : "",
      photo_5_url: data?.photo_5_url ? data.photo_5_url : "",
      company: {
        id: permissions.companyId
      }
    };
    if (isAddOn) {
      await deleteMany("ServiceAddOn", {
        ids: data?.core_services?.map((service: any) => service.id)
      });
      const addOnServicesPayload = data[
        "@@ra-many-to-many/AddOns/ServiceAddOn/Service"
      ]?.map((addOnServiceId: string) => {
        return {
          id: addOnServiceId
        };
      });
      for (const addOnService of addOnServicesPayload) {
        const addOnPayload = {
          core_service: { id: data.id },
          add_on: { id: addOnService.id }
        };
        await create("ServiceAddOn", {
          data: addOnPayload
        });
      }
      await edit(
        "Service",
        {
          id: data.id,
          data: {
            name: data.name,
            price: data.price,
            scheduling_priority: data.scheduling_priority,
            is_addon: true,
            description: data.description,
            duration: data.duration,
            clean_up_time: data.clean_up_time,
            choice_is_required: data.choice_is_required
          }
        },
        {
          onSuccess: () => {
            setIsLoading(false);
            navigate("/dashboard/AddOns");
          },
          onError: () => {
            setIsLoading(false);
          }
        }
      );
    } else {
      // await deleteMany("FacilityService", {
      //   ids: data.facilities.map((facility: any) => facility.id)
      // });
      // const facilitiesPayload = data[
      //   "@@ra-many-to-many/Service/FacilityService/Facility"
      // ]?.map((facilityId: string) => {
      //   return {
      //     facility: {
      //       id: facilityId
      //     }
      //   };
      // });
      // if (facilitiesPayload) {
      //   payload.facilities = facilitiesPayload;
      // }
      submitWithPhoto(payload);
    }
  };

  const createValidationSchema = () => {
    const baseSchema = {
      name: yup.string().nullable().required("Name required."),
      price: yup.number().nullable().required("Price required."),
      duration: yup.number().nullable().required("Duration required.")
    };

    if (isAddOn) {
      const addOnSchema = {
        scheduling_priority: yup
          .string()
          .oneOf(
            AddOnsSchedulingPriorities.map((item) => item.id),
            "Invalid scheduling priority"
          )
          .required("Scheduling priority required."),
        "@@ra-many-to-many/AddOns/ServiceAddOn/Service": yup
          .array()
          .of(yup.string())
          .min(1, "At least one service must be selected")
          .required("Selected services are required.")
      };

      return yup
        .object()
        .shape({ ...baseSchema, ...addOnSchema })
        .required();
    }
    const serviceSchema = {
      clean_up_time: yup.number().nullable(),
      special_conditions: yup.string().nullable(),
      description: yup.string().nullable()
    };

    return yup
      .object()
      .shape({ ...baseSchema, ...serviceSchema })
      .required();
  };

  return (
    <Edit
      actions={
        <CustomHeaderActions
          title={
            isAddOn
              ? translate("pagenames.add_on")
              : translate("pagenames.service")
          }
        />
      }
    >
      <SimpleForm
        onSubmit={submit}
        resolver={yupResolver(createValidationSchema())}
        toolbar={<CustomToolbar isAddOn={isAddOn} isLoading={isLoading} />}
      >
        <ShowAndEditDetails
          title={
            isAddOn
              ? translate("services.add_on_details")
              : translate("services.service_details")
          }
          view="edit"
          icon={isAddOn ? <AddOnsDetailsIcon /> : <ServiceDetailsIcon />}
        />
        <TextInput source="name" label={translate("services.name")} fullWidth />
        <RichTextInput
          source="description"
          label={translate("services.description")}
          fullWidth
          style={{
            backgroundColor: "#fff"
          }}
        />
        <NumberInput
          source="price"
          label={translate("services.price")}
          fullWidth
          defaultValue={0}
        />
        <NumberInput
          source="duration"
          label={translate("services.duration")}
          fullWidth
          defaultValue={0}
          min={0}
          max={180}
          step={1}
        />
        {isAddOn && (
          <>
            <BooleanInput
              source="choice_is_required"
              label={translate("services.choice_is_required")}
              fullWidth
              defaultValue={false}
            />
            <SelectInput
              source="scheduling_priority"
              label={translate("services.scheduling_priority")}
              fullWidth
              choices={AddOnsSchedulingPriorities}
              required={true}
              InputLabelProps={{ required: false }}
            />
            <ReferenceManyToManyInput
              source="id"
              reference="Service"
              through="ServiceAddOn"
              using="service_id,add_on_service_id"
              filterChoices={{
                company_id: permissions.companyId,
                is_addon: false
              }}
              perPageChoices={3000}
            >
              <SelectArrayInput
                optionText={(value) => value.name}
                label={translate("facility.services")}
                fullWidth
              />
            </ReferenceManyToManyInput>
          </>
        )}
        {!isAddOn && (
          <>
            <NumberInput
              source="clean_up_time"
              label={translate("services.clean_up_time")}
              fullWidth
            />
            <SelectInput
              source="category"
              label={translate("services.category")}
              fullWidth
              choices={ServiceCategoryChoices}
              defaultValue="OTHER"
              required={true}
              InputLabelProps={{ required: false }}
            />
            <ServiceImages imageKey="logo" />
            <CustomImageInputWrapper label="Gallery (Recommended dimension 1125x632) ">
              {imageKeys.map((imageKey, index) => (
                <CustomEditableImageInput
                  key={imageKey}
                  source={imageKey}
                  height="140px"
                  badgeNumber={index + 1}
                />
              ))}
            </CustomImageInputWrapper>
            <TextInput
              source="special_conditions"
              label={translate("services.special_conditions")}
              multiline
              fullWidth
            />
            {/* <ReferenceManyToManyInput
              source="id"
              reference="Facility"
              through="FacilityService"
              using="service_id,facility_id"
              filterChoices={{ company_id: permissions.companyId }}
            >
              <SelectArrayInput
                optionText={(value) => value.name}
                label={translate("services.facilities")}
                fullWidth
              />
            </ReferenceManyToManyInput> */}
            <div className="w-full">
              <h5 className="ml-[10px]">
                {translate("pagenames.artist_exclusions")}
              </h5>
              <ArtistExclusions />
            </div>
          </>
        )}
      </SimpleForm>
    </Edit>
  );
}
